import { createFeatureSelector, createSelector } from '@ngrx/store';
import { getSelectedProjectId } from '../projectDocuments/projectDocuments.selectors';
import { cashflowFeatureKey, CashflowState } from './cashflow.reducer';
import {
  BudgetVsCommitGeneralCashflow,
  BudgetVsCommitProjectCashflow,
  BudgetVsPrimeGeneralCashflow,
  BudgetVsPrimeProjectCashflow,
  CASHFLOW_TYPES,
  CashflowData,
  MonthlyData,
  PrimeVsCommitGeneralCashflow,
  PrimeVsCommitProjectCashflow,
} from '@app/framework/cash-flow/cashflow-table/cashflow.types';
import { CASHFLOW_CONSTANTS } from '@app/pages/webapp/cash-flow/cash-flow.constants';
import { colors } from 'src/colors';

const selectCashflowState = createFeatureSelector<CashflowState>(cashflowFeatureKey);
const selectCashflowStatus = createSelector(selectCashflowState, (state) => state.status);
const selectCashflowType = createSelector(selectCashflowState, (state) => state.cashflowType);
const selectCashflowFilters = createSelector(selectCashflowState, (state) => state.filters);

const selectCashflowDataRaw = createSelector(selectCashflowState, (state) => state.cashflow);

const selectCashflowData = createSelector(
  selectCashflowDataRaw,
  selectCashflowType,
  getSelectedProjectId,
  (cashflowData, cashflowType, projectId): CashflowData | null => {
    const cashflowTypeData = cashflowData[cashflowType];
    let cashflow: unknown;
    if (cashflowTypeData instanceof Map) {
      cashflow = cashflowTypeData.get(projectId);
    } else {
      cashflow = cashflowTypeData;
    }
    if (!cashflow) return null;

    switch (cashflowType) {
      case CASHFLOW_TYPES.budgetVsCommitGeneral: {
        const cashflowTyped = cashflow as BudgetVsCommitGeneralCashflow;
        return budgetVsCommitGeneralToCashflowData(cashflowTyped);
      }
      case CASHFLOW_TYPES.budgetVsCommitsByProject: {
        const cashflowTyped = cashflow as BudgetVsCommitProjectCashflow;
        return budgetVsCommitsProjectToCashflowData(cashflowTyped);
      }
      case CASHFLOW_TYPES.budgetVsPrimeGeneral: {
        const cashflowTyped = cashflow as BudgetVsPrimeGeneralCashflow;
        return budgetVsPrimeGeneralToCashflowData(cashflowTyped);
      }
      case CASHFLOW_TYPES.budgetVsPrimesByProject: {
        const cashflowTyped = cashflow as BudgetVsPrimeProjectCashflow;
        return budgetVsPrimeProjectToCashflowData(cashflowTyped);
      }
      case CASHFLOW_TYPES.primeVsCommitGeneral: {
        const cashflowTyped = cashflow as PrimeVsCommitGeneralCashflow;
        return primeVsCommitGeneralToCashflowData(cashflowTyped);
      }
      case CASHFLOW_TYPES.primeVsCommitsByProject: {
        const cashflowTyped = cashflow as PrimeVsCommitProjectCashflow;
        return primeVsCommitProjectToCashflowData(cashflowTyped);
      }
    }
  },
);
const convertMonthlyDataToArr = (obj: MonthlyData, start?: number, end?: number): number[] => {
  if (!obj) {
    console.error('Monthly data is not available');
    return Array(12).fill(0);
  }

  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    if (month < start || month > end) {
      return 0;
    }

    return obj[String(month)] ?? 0;
  });
};

const budgetVsCommitGeneralToCashflowData = (
  cashflow: BudgetVsCommitGeneralCashflow,
): CashflowData => {
  const stats = CASHFLOW_CONSTANTS.COMMITMENT_STATS;
  stats[0].value = cashflow.cashflow.total.budget;
  stats[1].value = cashflow.cashflow.total.committed;
  stats[2].value = cashflow.cashflow.total.remaining;
  stats[3].value = cashflow.cashflow.total.paid;
  stats[4].value = cashflow.cashflow.total.to_pay;
  return {
    stats: stats,
    firstFutureMonth: cashflow.first_future_month,
    graph: {
      actualsCol: {
        color: colors['yellow-500'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_actuals,
          0,
          cashflow.first_future_month,
        ),
      },
      budgetCol: {
        color: colors['navy-300'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_budget),
      },
      forecastCol: {
        color: colors['navy-700'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_forecast,
          cashflow.first_future_month,
        ),
      },
    },
    items: cashflow.cashflow.projects.map((project) => ({
      id: project.id,
      name: project.title,
      yearTotal: project.forecast_yearly_total,
      expenses: convertMonthlyDataToArr(project.monthly_forecast),
    })),
    total: {
      yearTotal: cashflow.cashflow.total.committed,
      expenses: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_forecast),
    },
  };
};

const budgetVsCommitsProjectToCashflowData = (
  cashflow: BudgetVsCommitProjectCashflow,
): CashflowData => {
  const stats = CASHFLOW_CONSTANTS.COMMITMENT_STATS;
  stats[0].value = cashflow.cashflow.total.budget;
  stats[1].value = cashflow.cashflow.total.committed;
  stats[2].value = cashflow.cashflow.total.remaining;
  stats[3].value = cashflow.cashflow.total.paid;
  stats[4].value = cashflow.cashflow.total.to_pay;
  return {
    stats,
    firstFutureMonth: cashflow.first_future_month,
    graph: {
      actualsCol: {
        color: colors['yellow-500'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_actuals,
          0,
          cashflow.first_future_month,
        ),
      },
      budgetCol: {
        color: colors['navy-300'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_budget),
      },
      forecastCol: {
        color: colors['navy-700'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_forecast,
          cashflow.first_future_month,
        ),
      },
    },
    items: cashflow.cashflow.items.map((lineItem) => ({
      id: lineItem.id,
      name: lineItem.name,
      yearTotal: lineItem.yearly_total,
      expenses: convertMonthlyDataToArr(lineItem.monthly_forecast),
    })),
    total: {
      yearTotal: cashflow.cashflow.total.committed,
      expenses: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_forecast),
    },
  };
};

const budgetVsPrimeGeneralToCashflowData = (
  cashflow: BudgetVsPrimeGeneralCashflow,
): CashflowData => {
  const stats = CASHFLOW_CONSTANTS.PRIME_CONTRACT_STATS;
  stats[0].value = cashflow.cashflow.total.budget;
  stats[1].value = cashflow.cashflow.total.prime;
  stats[2].value = cashflow.cashflow.total.funding;
  stats[3].value = cashflow.cashflow.total.realized;
  stats[4].value = cashflow.cashflow.total.to_realize;
  return {
    stats,
    firstFutureMonth: cashflow.first_future_month,
    graph: {
      actualsCol: {
        color: colors['green-400'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_prime_actuals,
          0,
          cashflow.first_future_month,
        ),
      },
      budgetCol: {
        color: colors['navy-300'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_budget),
      },
      forecastCol: {
        color: colors['navy-700'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_prime,
          cashflow.first_future_month,
        ),
      },
    },
    items: cashflow.cashflow.projects.map((project) => ({
      id: project.id,
      name: project.title,
      yearTotal: project.prime_yearly_total,
      expenses: convertMonthlyDataToArr(project.monthly_prime),
    })),
    total: {
      yearTotal: cashflow.cashflow.total.prime,
      expenses: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_prime),
    },
  };
};

const budgetVsPrimeProjectToCashflowData = (
  cashflow: BudgetVsPrimeProjectCashflow,
): CashflowData => {
  const stats = CASHFLOW_CONSTANTS.PRIME_CONTRACT_STATS;
  stats[0].value = cashflow.cashflow.total.budget;
  stats[1].value = cashflow.cashflow.total.prime;
  stats[2].value = cashflow.cashflow.total.funding;
  stats[3].value = cashflow.cashflow.total.realized;
  stats[4].value = cashflow.cashflow.total.to_realize;
  return {
    stats,
    firstFutureMonth: cashflow.first_future_month,
    graph: {
      actualsCol: {
        color: colors['green-400'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_prime_actuals,
          0,
          cashflow.first_future_month,
        ),
      },
      budgetCol: {
        color: colors['navy-300'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_budget),
      },
      forecastCol: {
        color: colors['navy-700'],
        data: convertMonthlyDataToArr(
          cashflow.cashflow.total.monthly_prime,
          cashflow.first_future_month,
        ),
      },
    },
    items: cashflow.cashflow.items.map((lineItem) => ({
      id: lineItem.id,
      name: lineItem.name,
      yearTotal: lineItem.yearly_total,
      expenses: convertMonthlyDataToArr(lineItem.monthly_prime),
    })),
    total: {
      yearTotal: cashflow.cashflow.total.prime,
      expenses: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_prime),
    },
  };
};

const primeVsCommitGeneralToCashflowData = (
  cashflow: PrimeVsCommitGeneralCashflow,
): CashflowData => {
  const stats = CASHFLOW_CONSTANTS.PRIME_V_COMMIT_STATS;
  stats[0].value = cashflow.cashflow.total.anticipated_fee;
  stats[1].value = cashflow.cashflow.total.prime;
  stats[2].value = cashflow.cashflow.total.anticipated_income;
  stats[3].value = cashflow.cashflow.total.committed;
  stats[4].value = cashflow.cashflow.total.anticipated_costs;
  return {
    stats,
    firstFutureMonth: cashflow.first_future_month,
    graph: {
      actualsCol: {
        color: colors['navy-700'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_forecast),
      },
      forecastCol: {
        color: colors['navy-300'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_prime),
      },
    },
    items: cashflow.cashflow.projects.map((project) => ({
      id: project.id,
      name: project.title,
      yearTotal: project.forecast_yearly_total,
      expenses: convertMonthlyDataToArr(project.monthly_forecast),
    })),
    total: {
      yearTotal: cashflow.cashflow.total.committed,
      expenses: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_forecast),
    },
  };
};

const primeVsCommitProjectToCashflowData = (
  cashflow: PrimeVsCommitProjectCashflow,
): CashflowData => {
  const stats = CASHFLOW_CONSTANTS.PRIME_V_COMMIT_STATS;
  stats[0].value = cashflow.cashflow.total.anticipated_fee;
  stats[1].value = cashflow.cashflow.total.prime;
  stats[2].value = cashflow.cashflow.total.anticipated_income;
  stats[3].value = cashflow.cashflow.total.committed;
  stats[4].value = cashflow.cashflow.total.anticipated_costs;
  return {
    stats,
    firstFutureMonth: cashflow.first_future_month,
    graph: {
      actualsCol: {
        color: colors['navy-700'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_forecast),
      },
      forecastCol: {
        color: colors['navy-300'],
        data: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_prime),
      },
    },
    items: cashflow.cashflow.items.map((lineItem) => ({
      id: lineItem.id,
      name: lineItem.name,
      yearTotal: lineItem.yearly_total,
      expenses: convertMonthlyDataToArr(lineItem.monthly_forecast),
    })),
    total: {
      yearTotal: cashflow.cashflow.total.committed,
      expenses: convertMonthlyDataToArr(cashflow.cashflow.total.monthly_forecast),
    },
  };
};

export const cashflowSelectors = {
  selectCashflowState,
  selectCashflowStatus,
  selectCashflowType,
  selectCashflowFilters,
  selectCashflowData,
};
